import { useEffect, useState } from 'react';
import { MdInstallMobile } from 'react-icons/md';

/**
 * InstallPWAButton
 *
 * Intercepts the browser's `beforeinstallprompt` event and exposes
 * a button so the user can install Toola as a PWA directly from the UI,
 * without going through an app store.
 *
 * The button is hidden:
 *  - when the app is already running in standalone mode (already installed)
 *  - on browsers that do not support the install prompt (e.g. Safari on iOS —
 *    on iOS the user must use "Add to Home Screen" from the Share menu)
 */
const InstallPWAButton = () => {
    const [installPrompt, setInstallPrompt] = useState<any>(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOS(isIOSDevice);

        // Already running as an installed PWA — no need to show the button
        if (
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone
        ) {
            setIsInstalled(true);
            return;
        }

        const handleBeforeInstall = (e: Event) => {
            // Prevent the default mini-infobar from appearing on mobile
            e.preventDefault();
            setInstallPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        const handleAppInstalled = () => {
            setIsInstalled(true);
            setInstallPrompt(null);
        };
        // Track when the app is successfully installed
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (!installPrompt) {
            if (isIOS) {
                window.alert(
                    'Pour installer Toola sur iPhone/iPad : bouton Partager puis « Sur l\'écran d\'accueil ».'
                );
            }
            return;
        }
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
            setInstallPrompt(null);
        }
    };

    // Hide only if already installed.
    // On iOS, keep the button visible and show manual install instructions.
    if (isInstalled || (!installPrompt && !isIOS)) return null;

    return (
        <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 rounded-lg border border-toola-500 px-2.5 py-1.5 text-xs font-semibold text-toola-500 transition hover:bg-toola-500 hover:text-white sm:gap-2 sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm"
            title="Install the Toola app"
        >
            <MdInstallMobile className="text-base sm:text-lg" />
            {isIOS ? 'Ajouter à l’accueil' : 'Installer'}
        </button>
    );
};

export default InstallPWAButton;
